import { useCallback, useState } from 'react';

export type Taille = 'petite' | 'moyenne' | 'grande';

export const TAILLES: readonly Taille[] = ['petite', 'moyenne', 'grande'];
export const TAILLE_DEFAUT: Taille = 'moyenne';

/**
 * Taille des vignettes (liste et cartes) choisie par la personne : une
 * préférence locale, par compte et par navigateur, comme `vueStorage.ts`.
 */
export function tailleStorageKey(compteId: string): string {
  return `artosera:oeuvres-taille:v1:${encodeURIComponent(compteId)}`;
}

// Stockage refusé : le choix reste actif tant que la page vit.
const volatile = new Map<string, Taille>();

/** Vide les choix tenus en mémoire faute de stockage (pour les tests). */
export function oublierTaillesVolatiles(): void {
  volatile.clear();
}

function estTaille(valeur: unknown): valeur is Taille {
  return valeur === 'petite' || valeur === 'moyenne' || valeur === 'grande';
}

/** Le choix du compte dans ce navigateur, ou `null` s'il n'en a pas fait. */
export function readTaille(compteId: string | null): Taille | null {
  if (!compteId || typeof window === 'undefined') return null;
  const cle = tailleStorageKey(compteId);
  const enMemoire = volatile.get(cle);
  if (enMemoire) return enMemoire;
  try {
    const valeur = window.localStorage.getItem(cle);
    return estTaille(valeur) ? valeur : null;
  } catch {
    return null;
  }
}

export function storeTaille(compteId: string, taille: Taille): void {
  const cle = tailleStorageKey(compteId);
  try {
    window.localStorage.setItem(cle, taille);
    volatile.delete(cle);
  } catch {
    volatile.set(cle, taille);
  }
}

/** La taille active : le choix du compte, sinon `moyenne`. */
export function useTaille(compteId: string | null): [Taille, (taille: Taille) => void] {
  const [choix, setChoix] = useState<Taille | null>(() => readTaille(compteId));
  const changer = useCallback(
    (taille: Taille): void => {
      setChoix(taille);
      if (compteId) storeTaille(compteId, taille);
    },
    [compteId],
  );
  return [choix ?? TAILLE_DEFAUT, changer];
}
